import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function toIsoLocal(local) {
  // datetime-local returns "YYYY-MM-DDTHH:MM" (local). Convert to ISO with local tz offset.
  if (!local) return "";
  const d = new Date(local);
  return d.toISOString();
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function NewEvent({ mode = "new" }) {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "Online",
    start_at_local: "",
    capacity: 0,
  });
  const [loaded, setLoaded] = useState(mode === "new");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode === "edit" && eventId) {
      api.get(`/events/${eventId}`).then(({ data }) => {
        const ev = data.event;
        setForm({
          title: ev.title,
          description: ev.description,
          location: ev.location,
          start_at_local: toLocalInput(ev.start_at),
          capacity: ev.capacity || 0,
        });
        setLoaded(true);
      });
    }
  }, [mode, eventId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        start_at: toIsoLocal(form.start_at_local),
        capacity: Number(form.capacity) || 0,
      };
      let result;
      if (mode === "new") {
        ({ data: result } = await api.post("/events", payload));
      } else {
        ({ data: result } = await api.patch(`/events/${eventId}`, payload));
      }
      toast.success(mode === "new" ? "Etkinlik oluşturuldu" : "Etkinlik güncellendi");
      navigate(`/events/${result.event_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="k-container py-16"><div className="k-card h-96 animate-pulse" /></div>;

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-5xl text-ink-900" data-testid="new-event-title">
          {mode === "new" ? "Yeni etkinlik" : "Etkinliği düzenle"}
        </h1>
        <p className="mt-2 font-body text-ink-700">Küçük gruplu, sıcak, ısıtan bir buluşma yarat.</p>

        <form onSubmit={submit} className="k-card mt-8 space-y-5 p-8" data-testid="new-event-form">
          <div>
            <label className="k-label">Başlık</label>
            <input
              className="k-input"
              required
              minLength={3}
              maxLength={140}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="new-event-title-input"
            />
          </div>
          <div>
            <label className="k-label">Açıklama</label>
            <textarea
              rows={6}
              className="k-input resize-none"
              required
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="new-event-description-input"
            />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="k-label">Yer</label>
              <input
                className="k-input"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="new-event-location-input"
              />
            </div>
            <div>
              <label className="k-label">Tarih & saat</label>
              <input
                type="datetime-local"
                className="k-input"
                required
                value={form.start_at_local}
                onChange={(e) => setForm({ ...form, start_at_local: e.target.value })}
                data-testid="new-event-datetime-input"
              />
            </div>
          </div>
          <div>
            <label className="k-label">Kontenjan (0 = sınırsız)</label>
            <input
              type="number"
              min={0}
              className="k-input"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              data-testid="new-event-capacity-input"
            />
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

            <button type="submit" disabled={busy} className="k-btn-primary disabled:opacity-60" data-testid="new-event-submit">
              {busy ? "Kaydediliyor…" : mode === "new" ? "Etkinliği yayınla" : "Kaydet"}
            </button>
        </form>
      </div>
    </div>
  );
}
